import { useNavigate } from 'react-router-dom';

/**
 * Higher-order component to provide navigate function to class components
 * Only needed for class components that can't use hooks directly
 */
export function withNavigate(Component) {
  return function WrappedComponent(props) {
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} />;
  };
}

/**
 * Extract URL parameter by name using URLSearchParams API
 * Falls back to legacy string splitting for backwards compatibility
 * @param {string} paramName - The parameter name to extract
 * @returns {string|null} - The parameter value or null if not found
 */
export function getUrlParameter(paramName) {
  const urlParams = new URLSearchParams(window.location.search);
  const paramValue = urlParams.get(paramName);

  // Fallback to legacy method if URLSearchParams doesn't find it
  if (!paramValue && window.location.href.includes('=')) {
    return window.location.href.split('=')[1];
  }

  return paramValue;
}

/**
 * Get the current route path (e.g., 'change-password', 'reservations')
 * @returns {string} - The current route path without leading slash
 */
export function getCurrentRoute() {
  return window.location.pathname.split('/')[1] || '';
}

/**
 * Check if currently on a specific route
 * @param {string} routeName - The route name to check (e.g., 'change-password')
 * @returns {boolean} - True if on the specified route
 */
export function isOnRoute(routeName) {
  return getCurrentRoute() === routeName;
}

/**
 * Extract reset password token from URL
 * @returns {string|null} - The reset password token
 */
export function getResetPasswordToken() {
  return getUrlParameter('reset_password_token');
}

/**
 * Extract invitation token from URL
 * @returns {string|null} - The invitation token
 */
export function getInvitationToken() {
  return getUrlParameter('invitation_token');
}

/**
 * Navigate to a specific route (for use in class components with navigate prop)
 * @param {function} navigate - The navigate function from useNavigate hook
 * @param {string} path - The path to navigate to
 */
export function navigateTo(navigate, path) {
  if (navigate && typeof navigate === 'function') {
    navigate(path);
  } else {
    console.error('Navigate function not available. Component may need withNavigate HOC.');
  }
}
